import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Layers, Plus, RefreshCw, Save, Send, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const blankPreset = {
  name: "Default stream popup",
  enabled: true,
  icon: "Sparkles",
  accent_color: "#FFFFFF",
  title_template: "{title}",
  message_template: "{message}",
  metadata_template: "{event_type}",
  severity: "default",
  duration: 7,
  priority: 0,
  placement: { anchor: "top-left", x: "", y: "", width: 620, height: "", margin: 36 },
  layout_preset: "black-bar-text",
  border_width: 2,
  media_reference: "",
};

const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";
const textareaClass = "w-full min-h-[92px] px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";

export default function PopupEditor() {
  const [presets, setPresets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(blankPreset);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [previewPayload, setPreviewPayload] = useState({
    display_name: "Test Viewer",
    username: "testviewer",
    event_type: "manual_preview",
    count: "",
    amount: "",
  });

  const selected = useMemo(
    () => presets.find((preset) => preset.id === selectedId) || null,
    [presets, selectedId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/popup-presets");
      const next = data.presets || [];
      setPresets(next);
      setSelectedId((current) => next.find((item) => item.id === current)?.id || next[0]?.id || "");
    } catch (err) {
      setError(readError(err, "Could not load popup presets."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (selected) {
      setForm(formFromPreset(selected));
    }
  }, [selected]);

  const createNew = () => {
    setSelectedId("");
    setForm({ ...blankPreset, placement: { ...blankPreset.placement } });
    setError("");
    setMessage("");
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy("save");
    setError("");
    setMessage("");
    try {
      const payload = payloadFromForm(form);
      const { data } = selected
        ? await api.patch(`/admin/popup-presets/${selected.id}`, payload)
        : await api.post("/admin/popup-presets", payload);
      setPresets((current) => {
        const exists = current.some((item) => item.id === data.id);
        return exists ? current.map((item) => (item.id === data.id ? data : item)) : [...current, data];
      });
      setSelectedId(data.id);
      setMessage(selected ? "Popup preset saved." : "Popup preset created.");
    } catch (err) {
      setError(readError(err, "Could not save popup preset."));
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.name}"? Alerts using it will have popup display turned off.`)) return;
    setBusy("delete");
    setError("");
    setMessage("");
    try {
      await api.delete(`/admin/popup-presets/${selected.id}`);
      setMessage("Popup preset deleted.");
      await load();
    } catch (err) {
      setError(readError(err, "Could not delete popup preset."));
    } finally {
      setBusy("");
    }
  };

  const test = async () => {
    setBusy("test");
    setError("");
    setMessage("");
    try {
      await api.post("/admin/popup-presets/test", {
        preset: payloadFromForm(form),
        ...previewPayload,
        count: optionalInteger(previewPayload.count),
        amount: optionalInteger(previewPayload.amount),
      });
      setMessage("Popup preview sent to the overlay.");
    } catch (err) {
      setError(readError(err, "Could not send popup preview."));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-popup-editor-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Unified overlay system</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Layers className="w-5 h-5 text-[hsl(var(--primary))]" />
            Popup Editor
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-3xl">
            Popups render as top-left white text for the stream overlay by default. The individual feature provides the event text.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={createNew} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white">
            <Plus className="w-3 h-3" /> New
          </button>
          <button onClick={load} disabled={loading || !!busy} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {(error || message) && (
        <div className={`surface p-4 text-sm ${error ? "text-[hsl(var(--destructive))]" : "text-emerald-300"}`}>{error || message}</div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6">
        <section className="surface overflow-hidden">
          <div className="p-5 border-b border-[hsl(var(--border))]">
            <div className="label-eyebrow">Popup presets</div>
            <h2 className="text-lg font-semibold mt-1">Reusable styles</h2>
          </div>
          {loading ? (
            <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">Loading popup presets...</div>
          ) : presets.length === 0 ? (
            <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">No popup presets yet. Create one to test the overlay.</div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setSelectedId(preset.id)}
                  className={`w-full text-left p-4 hover:bg-[hsl(var(--surface-2))] transition ${selected?.id === preset.id ? "bg-[hsl(var(--surface-2))]" : ""}`}
                >
                  <div className="font-medium truncate">{preset.name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {preset.enabled ? "Enabled" : "Disabled"} - top-left text
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-6">
          <form onSubmit={save} className="surface p-5 space-y-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="label-eyebrow">{selected ? "Edit popup preset" : "Create popup preset"}</div>
                <h2 className="text-xl font-semibold mt-1">{selected ? selected.name : "New popup preset"}</h2>
              </div>
              <div className="flex gap-2">
                {selected && (
                  <button type="button" onClick={remove} disabled={busy === "delete"} className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[hsl(var(--destructive))]/60 text-[hsl(var(--destructive))] text-sm disabled:opacity-40">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
                <button type="submit" disabled={busy === "save"} className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-40">
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_130px_130px] gap-3 items-end">
              <Field label="Preset name"><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className={inputClass} /></Field>
              <Field label="Text color"><input type="color" value={form.accent_color} onChange={(event) => setForm({ ...form, accent_color: event.target.value })} className="w-full h-10 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]" /></Field>
              <NumberField label="Duration" value={form.duration} onChange={(duration) => setForm({ ...form, duration })} />
              <label className="flex items-center gap-2 text-sm h-10">
                <input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} className="w-4 h-4" />
                Enabled
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Title template" help="Use {title} to let each alert or interaction provide its own title. Also supports {display_name}, {username}, {event_type}, {count}, and {amount}.">
                <textarea value={form.title_template} onChange={(event) => setForm({ ...form, title_template: event.target.value })} className={textareaClass} />
              </Field>
              <Field label="Message template" help="Use {message} for feature-specific text. Avoid hardcoded generic copy unless this preset is only for one event type.">
                <textarea value={form.message_template} onChange={(event) => setForm({ ...form, message_template: event.target.value })} className={textareaClass} />
              </Field>
              <Field label="Metadata template" help="Use {metadata} or {event_type} for a small context label.">
                <textarea value={form.metadata_template} onChange={(event) => setForm({ ...form, metadata_template: event.target.value })} className={textareaClass} />
              </Field>
            </div>

          </form>

          <section className="surface p-5 space-y-4">
            <div>
              <div className="label-eyebrow">Manual preview</div>
              <h2 className="text-lg font-semibold mt-1">Send popup to overlay</h2>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">This uses the same normalized popup request the future feature integrations will use.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <Field label="Display name"><input value={previewPayload.display_name} onChange={(event) => setPreviewPayload({ ...previewPayload, display_name: event.target.value })} className={inputClass} /></Field>
              <Field label="Username"><input value={previewPayload.username} onChange={(event) => setPreviewPayload({ ...previewPayload, username: event.target.value })} className={inputClass} /></Field>
              <Field label="Event type"><input value={previewPayload.event_type} onChange={(event) => setPreviewPayload({ ...previewPayload, event_type: event.target.value })} className={inputClass} /></Field>
              <Field label="Count"><input type="number" value={previewPayload.count} onChange={(event) => setPreviewPayload({ ...previewPayload, count: event.target.value })} className={inputClass} /></Field>
              <Field label="Amount"><input type="number" value={previewPayload.amount} onChange={(event) => setPreviewPayload({ ...previewPayload, amount: event.target.value })} className={inputClass} /></Field>
            </div>
            <button onClick={test} disabled={busy === "test"} className="inline-flex items-center gap-2 px-4 py-2 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm disabled:opacity-40">
              <Send className="w-4 h-4" /> {busy === "test" ? "Sending..." : "Send preview"}
            </button>
          </section>

          <PopupPreview form={form} />
        </section>
      </div>
    </div>
  );
}

function PopupPreview({ form }) {
  const accent = form.accent_color || "#FFFFFF";
  return (
    <section className="surface p-5">
      <div className="label-eyebrow mb-3 flex items-center gap-2"><Eye className="w-4 h-4" /> Local style preview</div>
      <div className="rounded-xl bg-black/80 p-4 min-h-36 overflow-hidden">
        <div
          className="max-w-4xl pl-2"
          style={{
            borderLeft: `4px solid ${accent}`,
            textShadow: "0 2px 8px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)",
          }}
        >
          <div className="font-black uppercase text-white" style={{ fontFamily: "Arial Black, Arial, Helvetica, sans-serif", fontSize: 30, lineHeight: 1.02, overflowWrap: "break-word" }}>
            {form.title_template || "Title"}
          </div>
          <div className="mt-2 text-[#f2f2f2] font-bold" style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: 17, lineHeight: 1.1, overflowWrap: "break-word" }}>
            {form.message_template || "Message"}
          </div>
          <div className="mt-1 truncate text-xs font-black uppercase" style={{ color: accent }}>
            {form.metadata_template || "metadata"}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label = "", help = "", children } = {}) {
  return (
    <label className="block text-sm">
      <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      {children}
      {help && <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{help}</p>}
    </label>
  );
}

function NumberField({ label = "", value, onChange } = {}) {
  return (
    <Field label={label}>
      <input type="number" value={value ?? ""} onChange={(event) => onChange(event.target.value === "" ? "" : Number(event.target.value))} className={inputClass} />
    </Field>
  );
}

function formFromPreset(preset) {
  return {
    ...blankPreset,
    ...preset,
    media_reference: preset.media_reference || "",
    placement: { ...blankPreset.placement, ...(preset.placement || {}) },
  };
}

function payloadFromForm(form) {
  return {
    ...form,
    media_reference: form.media_reference || null,
    placement: {
      anchor: form.placement?.anchor || "top-center",
      x: emptyToNull(form.placement?.x),
      y: emptyToNull(form.placement?.y),
      width: Number(form.placement?.width || 520),
      height: emptyToNull(form.placement?.height),
      margin: Number(form.placement?.margin || 36),
    },
  };
}

function emptyToNull(value) {
  return value === "" || value === undefined ? null : Number(value);
}

function optionalInteger(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? Math.trunc(number) : undefined;
}

function readError(err, fallback) {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || JSON.stringify(item)).join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  if (err.response?.status) return `${fallback} (${err.response.status})`;
  if (err.message) return `${fallback}: ${err.message}`;
  return fallback;
}
